/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type Record from 'linced-app/models/attendance/record';
import type Person from 'linced-app/models/person';
import type School from 'linced-app/models/school';

// Proto: attendance.Audit.
export default class Audit extends Model {
  declare [Type]: 'attendance/audit';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // schoolId is the unique identifier of the school the attendance audit log
  // relates to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // recordId is the unique identifier of the attendance record this audit
  // log is linked to.
  @belongsTo('attendance/record', { async: true, inverse: null })
  declare recordId: AsyncBelongsTo<Record>;

  @alias('recordId')
  declare record: AsyncBelongsTo<Record>;

  @alias('record.id')
  declare record_id: string | null | undefined;

  // date contains the date the attendance was recorded against as a string
  // to facilitate filtering.
  @attr('string')
  declare date: string | undefined;

  // originalAuthorId provides the unique identifier of the original
  // attendance marker.
  @belongsTo('person', { async: true, inverse: null })
  declare originalAuthorId: AsyncBelongsTo<Person>;

  @alias('originalAuthorId')
  declare originalAuthor: AsyncBelongsTo<Person>;

  // newAuthorId provides the unique identifier of the new attendance marker.
  @belongsTo('person', { async: true, inverse: null })
  declare newAuthorId: AsyncBelongsTo<Person>;

  @alias('newAuthorId')
  declare newAuthor: AsyncBelongsTo<Person>;

  // personId provides the unique identifier of the Person whose attendance
  // record was changed.
  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  // person provides a way to get the Person from either the audit (if present)
  // or the attendance record (if it still exists).
  @computed('personId', 'record.person')
  get person() {
    // The `personId` proxy is considered non-empty, so we need to check a
    // property of the Person rather than `personId` itself.
    if (isEmpty(this.get('personId.id'))) {
      return this.get('record.person');
    }
    return this.personId;
  }

  // originalCode provides the previously recorded attendance code.
  @attr('string', { defaultValue: '' })
  declare originalCode: string;

  // newCode provides the newly recorded attendance code.
  @attr('string', { defaultValue: '' })
  declare newCode: string;

  // reason provides a description as to why the attendance edit was made.
  @attr('string', { defaultValue: '' })
  declare reason: string;

  // effectiveFrom provides a timestamp from which the updated code applies.
  @attr('number')
  declare effectiveFrom: number | undefined;

  // oldNotes provides previously recorded notes.
  @attr('string')
  declare oldNotes: string | undefined;
}
